import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import MainFooter from '../../components/layout/MainFooter';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import Icon from '../../components/AppIcon';
import '../../styles/legal-pages.css';

const PrivacyPolicyPage = () => {
  const [expandedSection, setExpandedSection] = useState('introduction');

  const sections = {
    'introduction': {
      title: 'Introduction',
      icon: 'Info',
      content: [
        {
          heading: 'Our Privacy Commitment',
          text: 'VA Sprouts ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.'
        },
        {
          heading: 'Scope',
          text: 'This Privacy Policy applies to all services provided through the VA Sprouts platform, including but not limited to job posting, candidate search, messaging, skills assessment, payments, and analytics. Please read this policy carefully.'
        },
        {
          heading: 'Updates to This Policy',
          text: 'We may update this Privacy Policy from time to time. We will notify you of changes by updating the "Last Updated" date. Your continued use of the platform following such notification constitutes your acceptance of the updated policy.'
        }
      ]
    },
    'information-collection': {
      title: 'Information We Collect',
      icon: 'Database',
      content: [
        {
          heading: 'Information You Provide Directly',
          text: 'Account Registration: When you create an account, we collect your name, email address, phone number, role (Virtual Assistant or Employer), password, and profile information.\n\nProfile Information: VAs provide skills, experience, hourly rates, portfolio links, certifications, and profile photos. Employers provide company information, industry, team size, and job requirements.\n\nCommunication Data: Messages, email conversations, support tickets, and feedback you send through our platform.\n\nPayment Information: Billing address, payment method details (processed securely through Stripe).\n\nApplications & Job Data: When you apply for jobs or post positions, we collect job descriptions, cover letters, qualifications, and related information.'
        },
        {
          heading: 'Information Collected Automatically',
          text: 'Device Information: IP address, browser type, operating system, device ID.\n\nUsage Data: Pages visited, time spent, features used, searches performed, clicks, and interactions.\n\nCookies & Similar Technologies: We use cookies, pixels, and local storage to enhance your experience and track behavior.\n\nLocation Data: Approximate location based on IP address (not precise location unless you permit it).\n\nAnalytics: Google Analytics, Mixpanel, and similar tools track aggregated user behavior.'
        },
        {
          heading: 'Information From Third Parties',
          text: 'Social Logins: If you sign up via Google or LinkedIn, we receive basic profile information.\n\nPayment Processors: Stripe provides payment confirmation data.\n\nSkills Verification: Third-party assessment services may provide test results and verification data.'
        }
      ]
    },
    'information-use': {
      title: 'How We Use Your Information',
      icon: 'Settings',
      content: [
        {
          heading: 'Service Delivery',
          text: 'Creating and managing your account\nMatching VAs with job opportunities\nProcessing payments and invoices\nFacilitating messaging and communication\nProviding customer support'
        },
        {
          heading: 'Platform Improvement',
          text: 'Analyzing user behavior and trends\nTesting new features\nOptimizing user experience\nDiagnosing technical issues\nConducting research and analytics'
        },
        {
          heading: 'Communication',
          text: 'Sending service updates and announcements\nNotifying about account changes\nMarketing emails (with opt-out option)\nSecurity alerts\nSupport responses'
        },
        {
          heading: 'Safety & Compliance',
          text: 'Detecting fraud and abuse\nEnforcing our Terms of Service\nComplying with legal obligations\nProtecting platform security\nDispute resolution'
        },
        {
          heading: 'AI Matching & Recommendations',
          text: 'Our AI matching system analyzes your skills, experience, and preferences to provide personalized job recommendations and candidate matches. This includes analyzing job descriptions and VA profiles to calculate match percentages.'
        }
      ]
    },
    'information-sharing': {
      title: 'How We Share Your Information',
      icon: 'Share2',
      content: [
        {
          heading: 'Public Profile Information',
          text: 'For VAs: Your profile name, skills, experience, hourly rate, reviews, and portfolio may be visible to employers searching our platform. You can control profile visibility settings.'
        },
        {
          heading: 'Within Conversations',
          text: 'When you message another user, only that specific participant can see the conversation. Support staff may access conversations when investigating complaints or disputes.'
        },
        {
          heading: 'Service Providers',
          text: 'We share data with third-party vendors who assist our operations:\n• Stripe (payment processing)\n• SendGrid/Resend (email services)\n• Google Cloud (hosting and analytics)\n• Third-party assessment services\n\nAll vendors are bound by confidentiality agreements and process data according to our instructions.'
        },
        {
          heading: 'Legal Requirements',
          text: 'We may disclose information when required by law, court order, government request, or to protect our legal rights and safety.'
        },
        {
          heading: 'Business Transfers',
          text: 'If VA Sprouts is acquired, merged, or sells assets, your data may be transferred as part of that transaction. We will notify you of any such change.'
        },
        {
          heading: 'We Do NOT Sell Your Data',
          text: 'We do not sell, rent, or trade your personal information to third parties for their marketing purposes. Any data sharing is limited to what is necessary to provide our services.'
        }
      ]
    },
    'data-security': {
      title: 'Data Security',
      icon: 'Lock',
      content: [
        {
          heading: 'Security Measures',
          text: 'We implement industry-standard security practices:\n• SSL/TLS encryption for data in transit\n• Encrypted password storage\n• Role-based access controls\n• Regular security audits\n• Firewalls and intrusion detection\n• Data encryption at rest'
        },
        {
          heading: 'Your Responsibilities',
          text: 'You are responsible for maintaining the confidentiality of your password. Never share your login credentials. If you suspect unauthorized access, contact us immediately.'
        },
        {
          heading: 'Data Breach Notification',
          text: 'In the event of a data breach affecting your personal information, we will notify you within 30 days and provide guidance on protective measures.'
        }
      ]
    },
    'retention': {
      title: 'Data Retention',
      icon: 'Clock',
      content: [
        {
          heading: 'How Long We Keep Your Data',
          text: 'Active Account: Data is retained while your account is active and for as long as you use our services.'
        },
        {
          heading: 'After Account Deletion',
          text: 'After you delete your account, we retain:\n• Anonymized usage data for analytics\n• Transaction records for legal and tax compliance (7 years)\n• Dispute-related information\n• Archived messages (accessible only for compliance)\n\nPersonally identifiable information is deleted or anonymized within 30 days.'
        },
        {
          heading: 'Backup Data',
          text: 'Data in backups may be retained for up to 90 days before deletion.'
        }
      ]
    },
    'user-rights': {
      title: 'Your Privacy Rights',
      icon: 'Shield',
      content: [
        {
          heading: 'Access Your Data',
          text: 'You have the right to request a copy of all personal data we hold about you. Submit a request through your account settings or contact support.'
        },
        {
          heading: 'Correct Your Data',
          text: 'You can update your profile information, email, and preferences at any time through your account settings.'
        },
        {
          heading: 'Delete Your Data',
          text: 'You can request deletion of your account and associated data. Some data may be retained for legal compliance.'
        },
        {
          heading: 'Opt-Out of Marketing',
          text: 'You can unsubscribe from marketing emails at any time by clicking the unsubscribe link in the email or updating your notification preferences.'
        },
        {
          heading: 'GDPR Rights (EU Residents)',
          text: 'If you are in the EU, you have additional rights including data portability and objection to processing. Contact us at privacy@vasprouts.com for GDPR requests.'
        },
        {
          heading: 'CCPA Rights (California Residents)',
          text: 'California residents have the right to know, delete, and opt-out of sale of personal information. Use your account settings or contact privacy@vasprouts.com.'
        }
      ]
    },
    'cookies': {
      title: 'Cookies & Tracking',
      icon: 'Eye',
      content: [
        {
          heading: 'What Are Cookies?',
          text: 'Cookies are small files stored on your device that help us recognize you and enhance your experience.'
        },
        {
          heading: 'Types of Cookies We Use',
          text: 'Essential: Required for login and basic functionality\nPreferences: Remember your settings and preferences\nAnalytics: Track usage to improve the platform\nAdvertising: Show relevant content and ads'
        },
        {
          heading: 'Third-Party Analytics',
          text: 'We use Google Analytics, Mixpanel, and similar services to understand user behavior. These services have their own privacy policies.'
        },
        {
          heading: 'Cookie Controls',
          text: 'Most browsers allow you to block or delete cookies. Disabling certain cookies may limit platform functionality. You can also opt out of analytics tracking through your account settings.'
        }
      ]
    },
    'third-party-links': {
      title: 'Third-Party Links',
      icon: 'ExternalLink',
      content: [
        {
          heading: 'Links to Other Websites',
          text: 'Our platform may contain links to third-party websites. We are not responsible for their privacy practices. We encourage you to review their privacy policies before providing information.'
        },
        {
          heading: 'Social Media Integration',
          text: 'If you sign up via Google or LinkedIn, we follow their privacy standards for OAuth integration. You can disconnect social accounts from your settings.'
        }
      ]
    },
    'international': {
      title: 'International Data Transfers',
      icon: 'Globe',
      content: [
        {
          heading: 'Data Location',
          text: 'Your data is primarily stored on servers in the United States. If you are outside the US, by using our platform you consent to the transfer of your data to the US, where data protection laws may differ from your home country.'
        },
        {
          heading: 'GDPR Compliance',
          text: 'For EU residents, we use Standard Contractual Clauses to ensure adequate data protection when transferring data internationally.'
        }
      ]
    },
    'contact': {
      title: 'Contact Us',
      icon: 'Mail',
      content: [
        {
          heading: 'Questions or Requests',
          text: 'If you have questions about this Privacy Policy or wish to exercise your privacy rights, contact us at:\n\nEmail: privacy@vasprouts.com\nMailing Address: VA Sprouts, Inc.\nSupport Portal: /help-support\n\nWe will respond to your inquiry within 30 days.'
        }
      ]
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Privacy Policy | VA Sprouts</title>
        <meta name="description" content="Learn how VA Sprouts protects and manages your personal data and privacy." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <VAHeaderNav />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              We take your privacy seriously. Learn how we collect, use, and protect your personal information.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Table of Contents */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Navigation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(sections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => toggleSection(key)}
                className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-600 hover:shadow-md transition-all text-left"
              >
                <Icon name={section.icon} size={24} className="text-blue-600 flex-shrink-0" />
                <span className="font-medium text-gray-900 hover:text-blue-600 transition-colors text-sm">{section.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Policy Sections */}
        <div className="space-y-6">
          {Object.entries(sections).map(([key, section]) => (
            <div key={key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection(key)}
                className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon name={section.icon} size={24} className="text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
                </div>
                <Icon
                  name={expandedSection === key ? 'ChevronUp' : 'ChevronDown'}
                  size={24}
                  className="text-gray-500 transition-transform"
                />
              </button>

              {expandedSection === key && (
                <div className="border-t border-gray-200 px-6 py-6 space-y-6">
                  {section.content.map((item, idx) => (
                    <div key={idx}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.heading}</h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{item.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Important Notice */}
        <div className="mt-12 bg-amber-50 rounded-lg border border-amber-200 p-8">
          <div className="flex gap-4">
            <Icon name="AlertCircle" size={32} className="text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Your Rights & Responsibilities</h2>
              <ul className="space-y-2 text-gray-700">
                <li>✓ You have the right to access, correct, or delete your personal data</li>
                <li>✓ You can opt-out of marketing communications at any time</li>
                <li>✓ You are responsible for keeping your password confidential</li>
                <li>✓ Report data breaches or privacy concerns immediately</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Last Updated */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p><strong>Last updated:</strong> January 2026</p>
          <p>This policy is effective immediately upon posting. We may update it periodically.</p>
          <p className="mt-2">For questions: <Link to="/help-support" className="text-blue-600 hover:underline">Contact Support</Link></p>
        </div>
      </div>

      <MainFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
